<template>
  <v-card
    class="playposit-interaction full-width"
    :elevation="0"
    @click="isFeatureEnabled('clickEvent') ? $emit('interaction-click', $event) : false"
    :class="{
      'is-rubric': [
        'RubricSection',
        'RubricCriteria',
      ].includes(specialCase),
      'px-3': specialCase === 'RubricSection',
      'px-5': specialCase === 'RubricCriteria',
      'darkened': (
        isFeatureEnabled('hotspotStyling')
        && !!$_.get(localInteraction, 'position.dark')
      ),
    }"
    :dark="(
      isFeatureEnabled('hotspotStyling')
      && !!$_.get(localInteraction, 'position.dark')
    )"
    :color="backgroundColor"
  >
    <transition name="fade" mode="out-in">
      <div
        v-if="!isApiInitialized"
        class="playposit-interaction-loading-screen-wrapper"
      >
        <v-layout
          key="loadingScreen"
          class="playposit-interaction-loading-screen"
          justify-center
          align-center
        >
          <v-progress-circular
            indeterminate
            :size="64"
            color="primary"
          />
        </v-layout>
      </div>
      <template v-else>
        <div key="component">
          <template v-if="specialCase">
            <component
              v-if="showInteractionTopper"
              :is="selectedInteractionTopperComponent"
              v-bubble.snackbar.error
              v-bind="$props"
              :interaction.sync="finalizedInteraction"
              :interaction-attempt.sync="finalizedInteractionAttempt"
              :perspective-key="perspectiveComponentKey"
              :interaction-body-component-key="interactionTypeComponentKey + perspectiveComponentKey"
              :bulb-attempt.sync="finalizedBulbAttempt"
              :is-processing-data.sync="globalIsProcessingData"
            ></component>
            <component
              :is="specialCase"
              v-bubble.snackbar.error
              v-bind="$props"
              :anonymous="anonymous"
              :interaction.sync="finalizedInteraction"
              :interaction-attempt.sync="finalizedInteractionAttempt"
              :perspective-key="perspectiveComponentKey"
              :interaction-body-component-key="interactionTypeComponentKey + perspectiveComponentKey"
              :bulb-attempt.sync="finalizedBulbAttempt"
              :is-processing-data.sync="globalIsProcessingData"
              :root-teacher="rootTeacher"
            ></component>
          </template>
          <template v-else>
            <component
              v-if="showInteractionTopper"
              :is="selectedInteractionTopperComponent"
              v-bubble.snackbar.error
              v-bind="$props"
              :interaction.sync="finalizedInteraction"
              :interaction-attempt.sync="finalizedInteractionAttempt"
              :perspective-key="perspectiveComponentKey"
              :interaction-body-component-key="interactionTypeComponentKey + perspectiveComponentKey"
              :bulb-attempt.sync="finalizedBulbAttempt"
              :is-processing-data.sync="globalIsProcessingData"
            ></component>
            <component
              :is="selectedInteractionHeadComponent"
              v-bubble.snackbar.error
              v-bind="$props"
              :interaction.sync="finalizedInteraction"
              :interaction-attempt.sync="finalizedInteractionAttempt"
              :perspective-key="perspectiveComponentKey"
              :interaction-body-component-key="interactionTypeComponentKey + perspectiveComponentKey"
              :bulb-attempt.sync="finalizedBulbAttempt"
              :is-processing-data.sync="globalIsProcessingData"
              :token="token"
            ></component>
            <component
              v-if="selectedInteractionBodyComponent"
              :is="selectedInteractionBodyComponent"
              v-bubble.snackbar.error
              v-bind="$props"
              :interaction.sync="finalizedInteraction"
              :interaction-attempt.sync="finalizedInteractionAttempt"
              :bulb-attempt.sync="finalizedBulbAttempt"
              :is-processing-data.sync="globalIsProcessingData"
              :token="token"
              class="px-2 pb-2"
            ></component>
            <slot name="below"></slot>
          </template>
        </div>
      </template>
    </transition>
  </v-card>
</template>

<script>
/*
  GUIDE TO IMPORTING COMPONENTS

  The name that is given to the import is VERY important. It is used by the `selectedInteractionHeadComponent`
  and `selectedInteractionBodyComponent` computed values below so it is important that you name your import according
  to the rules. Import names should look like this:

  ${interactionType}${perspective}${componentType}

  interactionType
  ---------------
  Description:
    PascalCase version of the interaction type. See the `interactionTypeComponentKey` computed property for how these should look.

  Valid values:
    'MultipleChoice'
    'CheckAllThatApply'
    'FreeResponse'
    'FillInTheBlank'
    'FileViewer'
    'WebEmbed'
    'Discussion'
    'MultiselectPoll'
    'Poll'
    'Pause'

  perspective
  -----------
  Description:
    PascalCase version of the desired perspective. If you are not targetting a specific perspective, then 'Base'
    should be used in place of a perspective.

  Valid values:
    'DisplayMinimum'
    'DisplayMaximum'
    'Answered'
    'Unanswered'

  componentType
  -----------
  Description:
    PascalCase version of the interaction component type.

  Valid values:
    'InteractionHead'
    'InteractionBody'
*/

/*
  EXTERNAL
*/
import _ from 'lodash';

/*
  SERVICES
*/
import api from './services/api';

/*
  MIXINS
*/
import Interaction from './mixins/Interaction';

/*
  INTERACTION TOPPERS
*/
import WorksheetTopper from './components/Toppers/Worksheet.vue';
import ReviewTopper from './components/Toppers/Review.vue';
import TypeAndTimestampTopper from './components/Toppers/TypeAndTimestamp.vue';

/*
  INTERACTION HEAD
*/
// Default
import InteractionHead from './components/InteractionHead/InteractionHead.vue';
// Base
import WebEmbedBaseInteractionHead from './components/InteractionHead/Base/WebEmbed.vue';
import FileViewerBaseInteractionHead from './components/InteractionHead/Base/FileViewer.vue';
// DisplayMinimum
import FillInTheBlankDisplayMinimumInteractionHead from './components/InteractionHead/DisplayMinimum/FillInTheBlank.vue';
import WebEmbedDisplayMinimumInteractionHead from './components/InteractionHead/DisplayMinimum/WebEmbed.vue';
import FileViewerDisplayMinimumInteractionHead from './components/InteractionHead/DisplayMinimum/FileViewer.vue';
// Answered
import FillInTheBlankAnsweredInteractionHead from './components/InteractionHead/Answered/FillInTheBlank.vue';

/*
  INTERACTION BODY
*/
// DisplayMaximum
import CheckAllThatApplyDisplayMaximumInteractionBody from './components/InteractionBody/DisplayMaximum/CheckAllThatApply.vue';
import FillInTheBlankDisplayMaximumInteractionBody from './components/InteractionBody/DisplayMaximum/FillInTheBlank.vue';
import FreeResponseDisplayMaximumInteractionBody from './components/InteractionBody/DisplayMaximum/FreeResponse.vue';
import MultipleChoiceDisplayMaximumInteractionBody from './components/InteractionBody/DisplayMaximum/MultipleChoice.vue';
import MultiselectPollDisplayMaximumInteractionBody from './components/InteractionBody/DisplayMaximum/MultiselectPoll.vue';
import PollDisplayMaximumInteractionBody from './components/InteractionBody/DisplayMaximum/Poll.vue';

// DisplayMinimum
import CheckAllThatApplyDisplayMinimumInteractionBody from './components/InteractionBody/DisplayMinimum/CheckAllThatApply.vue';
import FreeResponseDisplayMinimumInteractionBody from './components/InteractionBody/DisplayMinimum/FreeResponse.vue';
import MultipleChoiceDisplayMinimumInteractionBody from './components/InteractionBody/DisplayMinimum/MultipleChoice.vue';
import MultiselectPollDisplayMinimumInteractionBody from './components/InteractionBody/DisplayMinimum/MultiselectPoll.vue';
import PollDisplayMinimumInteractionBody from './components/InteractionBody/DisplayMinimum/Poll.vue';

// Answered
import CheckAllThatApplyAnsweredInteractionBody from './components/InteractionBody/Answered/CheckAllThatApply.vue';
import FillInTheBlankAnsweredInteractionBody from './components/InteractionBody/Answered/FillInTheBlank.vue';
import FreeResponseAnsweredInteractionBody from './components/InteractionBody/Answered/FreeResponse.vue';
import MultipleChoiceAnsweredInteractionBody from './components/InteractionBody/Answered/MultipleChoice.vue';
import MultiselectPollAnsweredInteractionBody from './components/InteractionBody/Answered/MultiselectPoll.vue';
import PollAnsweredInteractionBody from './components/InteractionBody/Answered/Poll.vue';

/*
  SPECIAL CASES
*/
// Discussion
import Discussion from './components/SpecialCases/Discussion/Discussion.vue';

// Rubric
import RubricSection from './components/SpecialCases/Rubric/RubricSection.vue';
import RubricCriteria from './components/SpecialCases/Rubric/RubricCriteria.vue';

export default {
  name: 'PlaypositInteraction',
  components: {
    /*
      INTERACTION TOPPERS
    */
    WorksheetTopper,
    ReviewTopper,
    TypeAndTimestampTopper,
    /*
      INTERACTION HEAD
    */
    // Default
    InteractionHead,
    // Base
    WebEmbedBaseInteractionHead,
    FileViewerBaseInteractionHead,
    // DisplayMinimum
    WebEmbedDisplayMinimumInteractionHead,
    FileViewerDisplayMinimumInteractionHead,
    FillInTheBlankDisplayMinimumInteractionHead,
    // Answered
    FillInTheBlankAnsweredInteractionHead,
    /*
      INTERACTION BODY
    */
    // DisplayMaximum
    CheckAllThatApplyDisplayMaximumInteractionBody,
    FillInTheBlankDisplayMaximumInteractionBody,
    FreeResponseDisplayMaximumInteractionBody,
    MultipleChoiceDisplayMaximumInteractionBody,
    MultiselectPollDisplayMaximumInteractionBody,
    PollDisplayMaximumInteractionBody,
    // DisplayMinimum
    CheckAllThatApplyDisplayMinimumInteractionBody,
    FreeResponseDisplayMinimumInteractionBody,
    MultipleChoiceDisplayMinimumInteractionBody,
    MultiselectPollDisplayMinimumInteractionBody,
    PollDisplayMinimumInteractionBody,
    // AnsweredMinimum
    CheckAllThatApplyAnsweredInteractionBody,
    FillInTheBlankAnsweredInteractionBody,
    FreeResponseAnsweredInteractionBody,
    MultipleChoiceAnsweredInteractionBody,
    MultiselectPollAnsweredInteractionBody,
    PollAnsweredInteractionBody,
    /*
      SPECIAL CASES
    */
    // Discussion
    Discussion,
    // Rubric
    RubricSection,
    RubricCriteria,
  },
  mixins: [
    Interaction,
  ],
  props: {
    anonymous: Boolean,
    perspective: {
      type: String,
      required: true,
      validator(perspective) {
        return [
          'displayMinimum',
          'displayMaximum',
          'answered',
          'unanswered',
        ].includes(perspective);
      },
    },
    topper: {
      type: String,
      validator(topper) {
        return [
          'worksheet',
          'review',
          'typeAndTimestamp',
        ].includes(topper);
      },
    },
    interactionNumber: {
      type: Number,
      default: 1,
    },
    interactionId: Number,
    sectionId: Number,
    bulbId: Number,
    studentId: Number,
    teacherId: Number,
    setViewedComments: Function,
    backgroundColor: {
      type: String,
      default: 'white',
    },
    token: {
      type: String,
      required: false,
    },
  },
  data: () => ({
    isApiInitialized: false,
    globalIsProcessingData: false,
    localPerspective: null,
    componentData: {
      interaction: {},
      interactionAttempt: {},
      bulbAttempt: {},
    },
  }),
  computed: {
    finalizedInteraction() {
      if (!_.isEmpty(this.localInteraction)) return this.localInteraction;
      if (!_.isEmpty(_.get(this, 'componentData.interaction'))) return _.get(this, 'componentData.interaction');
      return {};
    },
    finalizedInteractionAttempt: {
      get() {
        if (!_.isEmpty(this.localInteractionAttempt)) return this.localInteractionAttempt;
        if (!_.isEmpty(_.get(this, 'componentData.interactionAttempt'))) return _.get(this, 'componentData.interactionAttempt');
        return null;
      },
      set(finalizedInteractionAttempt) {
        if (this.interactionAttempt) this.$emit('update:interaction-attempt', finalizedInteractionAttempt);
        else this.$set(this.componentData, 'interactionAttempt', finalizedInteractionAttempt);
      },
    },
    finalizedBulbAttempt() {
      if (!_.isEmpty(this.localBulbAttempt)) return this.localBulbAttempt;
      if (!_.isEmpty(_.get(this, 'componentData.bulbAttempt'))) return _.get(this, 'componentData.bulbAttempt');
      return null;
    },
    perspectiveComponentKey() {
      return _.upperFirst(this.localPerspective);
    },
    interactionTypeComponentKey() {
      if (this.finalizedInteraction.interactionType === 'multiple_choice') return 'MultipleChoice';
      else if (this.finalizedInteraction.interactionType === 'check_all_apply') return 'CheckAllThatApply';
      else if (this.finalizedInteraction.interactionType === 'free_response') return 'FreeResponse';
      else if (this.finalizedInteraction.interactionType === 'short_answer') return 'FillInTheBlank';
      else if (this.finalizedInteraction.interactionType === 'iframe_q') return 'WebEmbed';
      else if (this.finalizedInteraction.interactionType === 'file_viewer_q') return 'FileViewer';
      else if (this.finalizedInteraction.interactionType === 'discussion_q') return 'Discussion';
      else if (this.isMultiselectPoll) return 'MultiselectPoll';
      else if (this.finalizedInteraction.interactionType === 'poll_q') return 'Poll';
      return 'Pause';
    },
    isMultiselectPoll() {
      return (
        this.finalizedInteraction.interactionType === 'poll_q'
        && _.get(this, 'interaction.config.multiselect') === 1
      );
    },
    hasInteractionAttempt() {
      return (
        !!this.interactionAttempt
        && !_.isEmpty(this.interactionAttempt)
      );
    },
    componentNames() {
      return Object.keys(this.$options.components);
    },
    selectedInteractionTopperComponent() {
      if (!this.topper) return false;
      return `${_.upperFirst(this.topper)}Topper`;
    },
    selectedInteractionHeadComponent() {
      const interactionTypePerspectiveComponent = `${this.interactionTypeComponentKey}${this.perspectiveComponentKey}InteractionHead`;
      const interactionTypeComponent = `${this.interactionTypeComponentKey}BaseInteractionHead`;
      if (this.componentNames.includes(interactionTypePerspectiveComponent)) {
        return interactionTypePerspectiveComponent;
      } else if (this.componentNames.includes(interactionTypeComponent)) {
        return interactionTypeComponent;
      }
      return 'InteractionHead';
    },
    selectedInteractionBodyComponent() {
      const interactionTypePerspectiveComponent = `${this.interactionTypeComponentKey}${this.perspectiveComponentKey}InteractionBody`;
      const interactionTypeComponent = `${this.interactionTypeComponentKey}BaseInteractionBody`;
      if (this.componentNames.includes(interactionTypePerspectiveComponent)) {
        return interactionTypePerspectiveComponent;
      } else if (this.componentNames.includes(interactionTypeComponent)) {
        return interactionTypeComponent;
      }
      return false;
    },
    specialCase() {
      if (
        [
          'unanswered',
          'answered',
        ].includes(this.perspective)
        && this.finalizedInteraction.interactionType === 'discussion_q'
        && this.enabledFeatures.includes('fullDiscussion')
      ) return 'Discussion';
      else if (_.get(this, 'finalizedInteraction.config.template_key') === 'rubric') {
        if (this.finalizedInteraction.interactionType === 'pause_q') return 'RubricSection';
        else if (this.finalizedInteraction.interactionType === 'short_answer') return 'RubricCriteria';
      }
      return false;
    },
    requiredRemoteData() {
      const interaction = _.every([
        (
          !_.isObject(this.interaction)
          || _.isEmpty(this.interaction)
        ),
        _.isFinite(this.interactionId),
      ]);
      const interactionAttempt = _.every([
        (
          !_.isObject(this.interactionAttempt)
          || _.isEmpty(this.interactionAttempt)
        ),
        _.isFinite(this.interactionId),
        _.isFinite(this.bulbId),
        _.isFinite(this.studentId),
        _.isFinite(this.sectionId),
      ]);
      return {
        answered: {
          interaction,
          interactionAttempt,
        },
        displayMaximum: {
          interaction,
        },
      };
    },
    requiresRemoteData() {
      return Object.values(this.requiredRemoteData[this.perspective] || { placeholder: false })
        .every((value) => !!value);
    },
    needsToInitializeApi() {
      return (
        this.localPerspective === 'unanswered'
        || this.requiresRemoteData
      );
    },
    showInteractionTopper() {
      return (
        !!this.selectedInteractionTopperComponent
        && (
          this.perspective !== 'answered'
          || !this.permissionKeys.includes('hideInteractionHeaderInBulbResults')
        )
      );
    },
  },
  methods: {
    isFeatureEnabled(featureKey) {
      return this.enabledFeatures.includes(featureKey);
    },
    initializeProps() {
      this.localPerspective = (
        this.perspective === 'answered'
        && !this.hasInteractionAttempt
        && !this.requiresRemoteData
      ) ? 'displayMinimum' : this.perspective;
    },
    initializeInteractionAttempt() {
      // If there's an interactionAttempt and its interactionOptionIds column is a comma-separated string, we want to split that out into an array of numbers.
      if (_.isString(_.get(this, 'finalizedInteractionAttempt.interactionOptionIds'))) {
        _.set(
          this,
          'finalizedInteractionAttempt.interactionOptionIds',
          this.finalizedInteractionAttempt.interactionOptionIds
            .split(',')
            .map(Number)
            .filter(_.isFinite),
        );
      }
    },
    async getComponentData() {
      try {
        this.isApiInitialized = false;
        const { data: componentData } = await api.getComponentData({
          requiredModels: Object.keys(this.requiredRemoteData[this.perspective] || {}),
          ...this.localPerspective !== 'answered' && { mode: 'worksheet' },
        });
        this.$set(this, 'componentData', componentData);
        if (componentData.interactionAttempt) this.initializeInteractionAttempt();
        this.isApiInitialized = true;
      } catch(e) {
        this.$emit('error', e);
      }
    },
    async initializeApi() {
      this.isApiInitialized = false;
      const serverUrl = this.$getServerUrl();
      api.init({
        serverUrl,
        token: this.token,
        bulbId: this.bulbId || _.get(this, 'finalizedInteraction.bulbId'),
        sectionId: this.sectionId || _.get(this, 'bulbAttempt.sectionId'),
        interactionId: this.interactionId || _.get(this, 'finalizedInteraction.id'),
        studentId: this.studentId || _.get(this, 'bulbAttempt.studentId'),
      });
      if (this.requiresRemoteData) await this.getComponentData();
      else this.isApiInitialized = true;
    },
  },
  watch: {
    token(n, o) {
      if (
        !!n
        && !!o
      ) this.initializeApi();
    },
    globalIsProcessingData(n) {
      this.$emit('is-processing-data', n);
    },
    isApiInitialized(n) {
      this.$emit('loaded', n);
    },
  },
  async created() {
    this.initializeProps();
    this.initializeInteractionAttempt();
    if (this.needsToInitializeApi) await this.initializeApi();
    else this.isApiInitialized = true;
    if (
      _.get(this, 'finalizedInteractionAttempt.comment')
      && this.setViewedComments
    ) {
      let viewed;
      if (this.userType === 'teacher') {
        viewed = await api.updateTeacherCommentViews({
          interactionAttemptId: this.finalizedInteractionAttempt.id,
          sectionId: this.sectionId,
        });
      } else {
        viewed = await api.updateStudentCommentViews({
          interactionAttemptId: this.finalizedInteractionAttempt.id,
          bulbAttemptId: this.finalizedBulbAttempt.id,
        });
      }
      this.setViewedComments(viewed.data.id);
    }
  },
};
</script>

<style lang="scss">
  .playposit-interaction {
    width: 100%;
    overflow-y: auto;
    position: relative;
    border-bottom: 0 !important;

    p::after {
      content: '';
      clear: both;
      display: block;
    }

    table, th, tbody, td {
      // Need to apply a style to tables from the dash view since there is vendor css removing border values from tables.
      border: 1px solid black;
    }

    &:not(.is-rubric) {
      height: 100%;
    }

    &.is-rubric {
      .pp-rubric-section-text {
        font-weight: 700;
        font-size: 18px;
        border-bottom: 2px solid var(--branding-active-hex);
      }
      .pp-rubric-criteria {
        display: flex;
        margin-bottom: 8px;
      }
      .pp-rubric-criteria-text {
        width: 80%;
        font-size: 16px;
        display: flex;
        align-items: center;
      }
      .pp-rubric-criteria-input {
        width: 20%;
        display:flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: flex-start;
        margin-left: 10px;
        text-align: center;
      }

      .pp-rubric-criteria-input::after {
        color: var(--branding-active-hex);
        font-size: 11px;
      }

      .out-of-1 .pp-rubric-criteria-input::after {
        content: 'out of 1 points';
      }

      .out-of-2 .pp-rubric-criteria-input::after {
        content: 'out of 2 points';
      }

      .out-of-3 .pp-rubric-criteria-input::after {
        content: 'out of 3 points';
      }

      .out-of-4 .pp-rubric-criteria-input::after {
        content: 'out of 4 points';
      }

      .out-of-5 .pp-rubric-criteria-input::after {
        content: 'out of 5 points';
      }

      .pp-rubric-criteria-input input {
        margin-bottom: 0 !important;
        width: 72px !important;
      }

      .pp-rubric-criteria-input input {
         text-align: center;
      }
    }

    .overflow-x-hidden {
      overflow-x: hidden;
    }

    span {
      color: inherit;
    }

    * {
      text-align: left;
    }

    label {
      width: 100%;
    }

    label,
    label p {
      margin-bottom: 0;
    }

    .readonly-jump {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .imitation-label {
      color: rgba(0,0,0,.54);
      line-height: 20px;
      font-size: 16px;
      min-height: 8px;
    }

    .answer-line {
      height: 30px;
      border-bottom: 1px solid #DDD;
    }

    .rotate-45deg {
      transform: rotate(45deg);
    }

    .pb-30 {
      padding-bottom: 30px !important;
    }

    .px-1rem {
      padding-left: 1rem !important;
      padding-right: 1rem !important;
    }

    .justify-center {
      justify-content: center !important;
    }

    .align-start {
      align-items: flex-start !important;
    }

    img.responsive {
      max-width: calc(100% - 10px);
      max-height: 500px;
      margin-left: 5px;
      height: auto;
    }

    .playposit-interaction-loading-screen-wrapper {
      height: 80px;
    }

    .playposit-interaction-loading-screen {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      z-index: 1;
      background-color: rgba(255,255,255,0.75);
    }

    .playposit-answer {
      .interaction-feedback {
        margin-left: 0;
      }
    }

    .interaction-head {
      input {
        border-bottom: 1px solid;
        min-width: 112px;
        text-align: center;
        margin-bottom: 8px;
        color: rgba(0,0,0,0.38);

        &.correct {
          border-color: #4CAF50;
        }

        &.incorrect {
          border-color: #F44336;
        }
      }
    }

    .interaction-body {
      .poll {
        &.poll-display-maximum {
          .poll-option {
            p {
              margin-bottom: 0;
            }
          }
        }
      }
    }

    .playposit-answer {
      .interaction-feedback {
        margin-left: 9px;
        padding-left: 39px;

        &.neutral {
          background-color: #EEEEEE;
          border-left: 5px solid #9E9E9E;

          .correct-incorrect-label {
            color: #9E9E9E;
          }
        }
      }

      &.neutral,
      &.feedback {
        background-color: #EEEEEE;
      }

      &.correct {
        background-color: #E8F5E9;
      }

      &.incorrect {
        background-color: #FFEBEE;
      }

      &.missed {
        background-color: #FFFDE7;
      }

      &.ungraded {
        background-color: #E3F2FD;
      }
    }

    .multiple-choice,
    .check-all-that-apply,
    .poll {
      .v-input--selection-controls .v-input__control {
        width: 100%;
      }
      label {
        margin-bottom: 0;

        .pp-interaction-body p:last-of-type {
          margin-bottom: 0;
        }
      }
    }

    &:not(.darkened) {
      .multiple-choice,
      .check-all-that-apply,
      .poll {
        label {
          color: rgba(0,0,0,0.87);
        }
      }
    }

    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0.15s;
    }

    .fade-enter,
    .fade-leave-to {
      opacity: 0 !important;
    }
  }

  .v-chip {
    &.leading-button-chip {
      .v-chip__content {
        padding-left: 4px !important;

        .leading {
          margin-left: 2px;
        }
      }
    }

    &.no-focus-chip {
      &:focus {
        &::after {
          display: none;
        }
        box-shadow: none !important;
      }
    }

    &.pointer {
      .v-chip__content {
        cursor: pointer;
      }
    }
  }

  .pp-interaction-title,
  .pp-interaction-body,
  .pp-interaction-feedback {
    font-weight: 400;
    line-height: 1.5;
  }

  @media screen and (min-width: 0px) and (max-width: 320px) {
    .pp-interaction-title,
    .pp-interaction-body,
    .pp-interaction-feedback {
      font-size: 14px;
    }
  }
  @media screen and (min-width: 321px) and (max-width: 480px) {
    .pp-interaction-title,
    .pp-interaction-body,
    .pp-interaction-feedback {
      font-size: 14px;
    }
  }
  @media screen and (min-width: 481px) and (max-width: 768px) {
    .pp-interaction-title {
      font-size: 15px;
    }
    .pp-interaction-body,
    .pp-interaction-feedback {
      font-size: 14px;
    }
  }
  @media screen and (min-width: 769px) and (max-width: 1024px) {
    .pp-interaction-title {
      font-size: 16px;
    }
    .pp-interaction-body,
    .pp-interaction-feedback {
      font-size: 15px;
    }
  }
  @media screen and (min-width: 1025px) {
    .pp-interaction-title,
    .pp-interaction-body {
      font-size: 17px;
    }
    .pp-interaction-feedback {
      font-size: 16px;
    }
  }

  @media screen and (min-width: 0px) and (max-width: 320px) {
    #playposit-shared-styles {
      .pp-interaction-title,
      .pp-interaction-body {
        font-size: 14px;
      }
    }
  }
  @media screen and (min-width: 321px) and (max-width: 480px) {
    #playposit-shared-styles {
      .pp-interaction-title,
      .pp-interaction-body {
        font-size: 14px;
      }
    }
  }
  @media screen and (min-width: 481px) and (max-width: 768px) {
    #playposit-shared-styles {
      .pp-interaction-title {
        font-size: 15px;
      }
      .pp-interaction-body {
        font-size: 14px;
      }
    }
  }
  @media screen and (min-width: 769px) and (max-width: 1024px) {
    #playposit-shared-styles {
      .pp-interaction-title {
        font-size: 16px;
      }
      .pp-interaction-body {
        font-size: 15px;
      }
    }
  }
  @media screen and (min-width: 1025px) {
    #playposit-shared-styles {
      .pp-interaction-title {
        font-size: 17px;
      }
      .pp-interaction-body {
        font-size: 16px;
      }
    }
  }
</style>
